import {
    ReactElement,
    useEffect,
    useState,
} from 'react';

import {
    SimpleGrid,
    Table,
} from '@mantine/core';

import {usePrices} from '../hooks/usePrices.ts';
import {PaginationControls} from '../../../components/PaginationControls';

import {
    RowSelectionDataCell,
    SimpleTableBatchAction,
    TableBody,
    TableBodyColumn,
    TableHeaderColumn,
    TableHeaderRow,
} from '@flexinet/ui-components';
import {IPriceTableColumn, Price,} from '../types';
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {useParams} from "react-router-dom";
import {rbacActions} from '../../../config/rbac/actions.ts';
import {getColumns} from '../tables/columns';
import {FormDrawer} from '../drawers/FormDrawer';
import {update} from '../services/PricesService';
import {useQueryClient} from '@tanstack/react-query';

interface RecordsTableProps {
    selectedRecords?: Price[];
    setSelectedRecords?: (records: Price[]) => void;
    extraTools?: ReactElement[];
    settings?: { label: string, onClick: () => void }[];
}

export const RecordsTable = ({
                                 selectedRecords = [],
                                 setSelectedRecords,
                                 extraTools,
                                 settings,
                             }: RecordsTableProps) => {

        const {id: productId} = useParams();

        const {allowedActions} = useVerifiedPermissions()
        const queryClient = useQueryClient();

        const {
            visibleItems,
            loading,
            fetchRecords,
            handleFilterByKeyword,
            currentPage,
            hasNext,
            hasPrevious,
            goToNextPage,
            goToPreviousPage,
        } = usePrices(productId);

        const [drawerOpened, setDrawerOpened] = useState(false);
        const [selectedRecord, setSelectedRecord] = useState<Price | null>(null);

        useEffect(() => {
            fetchRecords();
        }, [productId]);

        const selection: Record<string, Price> = {};
        for (const record of selectedRecords) {
            selection[record.id] = record;
        }

        const handleRowSelection = (row: { id: string }) => {
            if (!setSelectedRecords) return;
            if (selection[row.id]) {
                setSelectedRecords(selectedRecords.filter(r => r.id !== row.id));
            } else {
                const fullRecord = visibleItems.find(item => item.id === row.id);
                if (fullRecord) {
                    setSelectedRecords([...selectedRecords, fullRecord]);
                }
            }
        };

        const handleSelectAllRows = () => {
            if (!setSelectedRecords) return;
            if (selectedRecords.length === visibleItems.length) {
                setSelectedRecords([]);
            } else {
                setSelectedRecords([...visibleItems]);
            }
        };

        const handleRecordClick = (record: Price) => {
            setSelectedRecord(record);
            setDrawerOpened(true);
        };

        const handleDrawerSubmit = async (values: Price) => {
            if (selectedRecord?.id) {
                const payload = {...values} as Record<string, unknown>;
                delete payload.created;
                delete payload.updated;
                delete payload.recurring;
                await update(selectedRecord.id, payload as Price);
                queryClient.invalidateQueries({queryKey: ['prices']});
            }
        };

        const tableColumns = getColumns(handleRecordClick);

        const getColumnsWithSelection = () => {
            let cols: IPriceTableColumn[] = [];
            if (allowedActions.includes(rbacActions.DeletePrice)) {

                cols.push({
                              id: 'select',
                              title: '',
                              render: item => {
                                  return <RowSelectionDataCell item={item} handleChange={handleRowSelection}/>;
                              },
                          },
                );
            }

            cols = cols.concat(tableColumns);

            return cols;

        };


        return (
            <SimpleGrid verticalSpacing='xl' m='sm'>
                <SimpleTableBatchAction
                    limit={10}
                    handleItemsPerPageChange={() => {}}
                    handleFilterByKeyword={handleFilterByKeyword}
                    settings={settings}
                    extraTools={extraTools}
                />
                <Table verticalSpacing='sm' striped highlightOnHover>
                    <TableHeaderRow
                        columns={getColumnsWithSelection() as TableHeaderColumn[]}
                        handleSelectAllRows={handleSelectAllRows}
                    />
                    <TableBody
                        items={visibleItems}
                        loading={loading}
                        selection={selection}
                        columns={getColumnsWithSelection() as TableBodyColumn[]}
                    />
                </Table>
                <PaginationControls
                    currentPage={currentPage}
                    hasNext={hasNext}
                    hasPrevious={hasPrevious}
                    onNext={goToNextPage}
                    onPrevious={goToPreviousPage}
                    loading={loading}
                />

                <FormDrawer
                    record={selectedRecord || {} as Price}
                    handleSubmit={handleDrawerSubmit}
                    opened={drawerOpened}
                    onClose={() => setDrawerOpened(false)}
                />
            </SimpleGrid>
        );
    }
;
